import { useEffect, useState } from 'react';
import { useTheme, alpha } from '@mui/material';
import { useResourceStore } from '../store/ResourceStore';
import { useCompactUI } from '../Gui/components/compactUi';

export default function DemoSceneSwitcher() {
    const [scenes, setScenes] = useState<string[]>([]);
    const currentScene = useResourceStore((state) => state.demoSceneName);
    const isCompactUI = useCompactUI();
    const theme = useTheme();
    const isLight = theme.palette.mode === 'light';

    const columnBg = alpha(theme.palette.background.default, isLight ? 0.45 : 0.3);
    const panelBg = alpha(theme.palette.background.paper, isLight ? 0.26 : 0.5);
    const panelBorder = isLight ? alpha(theme.palette.background.paper, 0.6) : alpha(theme.palette.common.white, 0.08);
    const panelColor = theme.palette.text.primary;
    const dividerColor = alpha(theme.palette.text.primary, 0.25);

    useEffect(() => {
        (async () => {
            try {
                const response = await fetch('/api/demo');
                if (response.ok) {
                    const data = (await response.json()) as string[];
                    setScenes(data);
                }
            } catch {
                // Silently fail — scene list is non-critical
            }
        })();
    }, []);

    const handleSceneChange = (sceneName: string) => {
        const url = new URL(window.location.href);
        url.searchParams.set('scene', sceneName);
        window.location.href = url.toString();
    };

    if (isCompactUI || scenes.length === 0) return null;

    return (
        <div
            data-guided-tour="guided-tour-scene-switcher"
            style={{
                position: 'absolute',
                top: 12,
                left: '50%',
                transform: 'translateX(-50%)',
                zIndex: 1200,
                pointerEvents: 'auto',
            }}
        >
            <div
                style={{
                    display: 'inline-block',
                    borderRadius: 12,
                    background: columnBg,
                    backdropFilter: 'blur(10px)',
                    WebkitBackdropFilter: 'blur(10px)',
                }}
            >
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0,
                        padding: '10px 12px',
                        borderRadius: 10,
                        background: panelBg,
                        border: `1px solid ${panelBorder}`,
                        color: panelColor,
                    }}
                >
                    {scenes.map((scene, index) => (
                        <div key={scene} style={{ display: 'flex', alignItems: 'center' }}>
                            {index > 0 && (
                                <div
                                    style={{
                                        width: 1,
                                        height: 16,
                                        background: dividerColor,
                                        margin: '0 10px',
                                        flexShrink: 0,
                                    }}
                                />
                            )}
                            <button
                                onClick={() => handleSceneChange(scene)}
                                style={{
                                    all: 'unset',
                                    cursor: scene === currentScene ? 'default' : 'pointer',
                                    fontSize: 12,
                                    lineHeight: '20px',
                                    padding: '2px 6px',
                                    borderRadius: 6,
                                    whiteSpace: 'nowrap',
                                    fontWeight: scene === currentScene ? 600 : 400,
                                    opacity: scene === currentScene ? 1 : 0.7,
                                    background: scene === currentScene ? alpha(panelColor, 0.1) : 'transparent',
                                    transition: 'opacity 0.15s, background 0.15s',
                                }}
                                onMouseEnter={(e) => {
                                    if (scene !== currentScene) {
                                        e.currentTarget.style.opacity = '1';
                                    }
                                }}
                                onMouseLeave={(e) => {
                                    if (scene !== currentScene) {
                                        e.currentTarget.style.opacity = '0.7';
                                    }
                                }}
                            >
                                {scene}
                            </button>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}
